import {
  Description,
  FindOutMore,
  ProductAttributes,
  TopBlock
} from "@growth/page.products";
import type {
  CoreProductInfo,
  Maybe,
  PricingOption,
  ProductPricingDetailsType,
  RelatedVariant
} from "@shared/graphql.types";
import { InfoBlock } from "@shared/ui.components";
import { Typography } from "@shared/ui.primitives";
import { useScreenViewport } from "lib/checkViewport";
import { useHasMounted } from "utils/wrappers";

import { PricingOptions } from "../PricingOptions/PricingOptions";
import { PricingOptionsExperiment2 } from "../PricingOptions/PricingOptionsExperiment2";
import { isNoDefaultExperiment } from "../PricingOptions/noDefaultExperiment.utils";

import * as styles from "./CoreProductInfoContainer.css";
import { useSidebarTopOffset } from "./hooks/useSidebarTopOffset";
import { useStickyProductInfo } from "./hooks/useStickyProductInfo";

export interface CoreProductInfoContainerProps {
  coreProductInfo: CoreProductInfo;
  filteredAttributes: string[];
  trackReviewsClick: () => void;
  onATB: ({
    membershipSKU,
    locationTypeDetails
  }: {
    membershipSKU: string;
    locationTypeDetails: string;
  }) => Promise<void>;
  handleWishlist: () => void;
  isInWishlist: boolean;
  relatedVariants?: Maybe<RelatedVariant[]>;
  selectedVariantSlug?: string;
  handleSubmit: ({ email }: { email: string }) => Promise<boolean>;
  submitted: boolean;
  membershipInBag: boolean;
  isMember: boolean;
  isFreeTrialVariant: boolean;
  promoBanner?: {
    title: string;
    colour: string;
  };
  promoPrice?: ProductPricingDetailsType | null;
  price?: ProductPricingDetailsType | null;
  onPriceChange?: ((price: PricingOption) => void) | null;
  noDefaultPageExperimentVariant?: number;
  freeTrialPATBVariant?: number;
  hasMembership?: boolean;
}

export const CoreProductInfoContainer = ({
  coreProductInfo,
  filteredAttributes,
  onATB,
  trackReviewsClick,
  handleWishlist,
  isInWishlist,
  relatedVariants,
  selectedVariantSlug,
  handleSubmit,
  submitted,
  membershipInBag,
  isMember,
  isFreeTrialVariant,
  promoBanner,
  promoPrice = null,
  price = null,
  onPriceChange = null,
  noDefaultPageExperimentVariant,
  freeTrialPATBVariant,
  hasMembership = false
}: CoreProductInfoContainerProps) => {
  const {
    infoBanner,
    brandName,
    title,
    subtitle,
    review,
    ribbons,
    description,
    isOutOfStock,
    membershipOptions,
    pricingOptions,
    sku,
    prices,
    isShadeMatchingEnabled
  } = coreProductInfo;
  // We conditionally pass this ref to either OutOfStock or PricingOptions
  const { ref, isScrolling, useSticky, showDescriptionAndAttributes } =
    useStickyProductInfo();

  const hasMounted = useHasMounted();
  const { isMobile } = useScreenViewport();
  const { sidebarOffsetTop } = useSidebarTopOffset(hasMounted, isMobile);

  // Determine container class based on sticky behavior
  const containerClassName = useSticky
    ? styles.coreProductInfoContainer
    : styles.nonStickyContainer;

  return (
    <div
      id="core-product-info-container"
      className={containerClassName}
      style={{
        top: `${sidebarOffsetTop}px`
      }}
    >
      <div
        className={`${
          isScrolling && useSticky ? styles.scrollingInnerContainer : ""
        } ${styles.innerContainer}`}
      >
        {showDescriptionAndAttributes && infoBanner && (
          <InfoBlock>
            <Typography color="primary-dark" variant="body-md">
              {infoBanner}
            </Typography>
          </InfoBlock>
        )}
        <TopBlock
          brandName={brandName ?? undefined}
          title={title}
          subtitle={subtitle || ""}
          rating={review.averageRating}
          reviewCount={review.count}
          ribbons={ribbons ?? undefined}
          trackReviewsClick={trackReviewsClick}
          promoBanner={promoBanner}
          price={price}
          promoPrice={promoPrice}
        />
        {showDescriptionAndAttributes && (
          <>
            <Description text={description ?? ""} />
            <ProductAttributes attributes={filteredAttributes} />
          </>
        )}
        {isNoDefaultExperiment(noDefaultPageExperimentVariant) ? (
          <PricingOptionsExperiment2
            membershipOptions={membershipOptions}
            pricingOptions={pricingOptions}
            variant={{ sku, prices }}
            onAddToBag={onATB}
            handleWishlist={handleWishlist}
            isInWishlist={isInWishlist}
            ref={ref}
            context="desktop"
            relatedVariants={relatedVariants}
            selectedVariantSlug={selectedVariantSlug}
            isOutOfStock={isOutOfStock}
            handleSubmit={handleSubmit}
            submitted={submitted}
            membershipInBag={membershipInBag}
            isMember={isMember}
            isFreeTrialVariant={isFreeTrialVariant}
            onPriceChange={onPriceChange}
            noDefaultPageExperimentVariant={
              noDefaultPageExperimentVariant as number
            }
            isShadeMatchingEnabled={isShadeMatchingEnabled}
            hasMembership={hasMembership}
          />
        ) : (
          <PricingOptions
            membershipOptions={membershipOptions}
            pricingOptions={pricingOptions}
            variant={{ sku, prices }}
            onAddToBag={onATB}
            handleWishlist={handleWishlist}
            isInWishlist={isInWishlist}
            ref={ref}
            context="desktop"
            relatedVariants={relatedVariants}
            selectedVariantSlug={selectedVariantSlug}
            isOutOfStock={isOutOfStock}
            handleSubmit={handleSubmit}
            submitted={submitted}
            membershipInBag={membershipInBag}
            isMember={isMember}
            isFreeTrialVariant={isFreeTrialVariant}
            onPriceChange={onPriceChange}
            freeTrialPATBVariant={freeTrialPATBVariant}
            isShadeMatchingEnabled={isShadeMatchingEnabled}
            hasMembership={hasMembership}
          />
        )}

        {!isScrolling && <FindOutMore id="find-out-more-anchor" />}
      </div>
    </div>
  );
};
